export type AudioCue = {
  id: string;
  category: 'movement' | 'combat' | 'objective' | 'base' | 'vehicle' | 'announcer';
  oscillatorFreq: number;
  oscillatorType: 'sine' | 'square' | 'sawtooth' | 'triangle';
  durationSeconds: number;
  volume: number;
  layers?: AudioCueLayer[];
  sampleUrl?: string;
  pitchEnd?: number;
  pitchRampSeconds?: number;
};

export type AudioPlaybackSettings = {
  masterVolume?: number;
  combatVolume?: number;
  movementVolume?: number;
  announcerVolume?: number;
  audioMuted?: boolean;
};

export type AudioCueLayer = {
  source?: 'oscillator' | 'noise';
  oscillatorFreq: number;
  oscillatorType: AudioCue['oscillatorType'];
  durationSeconds: number;
  volume: number;
  delaySeconds?: number;
  filterFreq?: number;
  filterFreqEnd?: number;
  filterRampSeconds?: number;
  filterType?: BiquadFilterType;
  pitchEnd?: number;
  pitchRampSeconds?: number;
};

export type AnnouncerEvent =
  | 'flag_taken'
  | 'flag_captured'
  | 'flag_returned'
  | 'flag_dropped'
  | 'you_have_flag'
  | 'double_kill'
  | 'triple_kill'
  | 'killing_spree'
  | 'match_start'
  | 'victory'
  | 'defeat'
  | 'generator_down'
  | 'generator_up'
  | 'vehicle_ready'
  | 'base_under_attack'
  | 'enemy_in_base'
  | 'need_medic';

// Phase 87: Tribes 2 signature announcer lines.
const ANNOUNCER_LINES: Record<AnnouncerEvent, string> = {
  flag_taken: 'Enemy flag taken!',
  flag_captured: 'Flag captured!',
  flag_returned: 'Our flag has been returned!',
  flag_dropped: 'Flag dropped!',
  you_have_flag: 'You have the flag! Return to base!',
  double_kill: 'Double kill!',
  triple_kill: 'Triple kill!',
  killing_spree: 'Killing spree!',
  match_start: 'Match starts now!',
  victory: 'Victory!',
  defeat: 'Mission failed.',
  generator_down: 'Generator destroyed!',
  generator_up: 'Generator online!',
  vehicle_ready: 'Vehicle ready.',
  base_under_attack: 'Base under attack!',
  enemy_in_base: 'Enemy in base!',
  need_medic: 'We need a medic!',
};

const AUDIO_CUES: AudioCue[] = [
  { id: 'skiing-start', category: 'movement', oscillatorFreq: 220, oscillatorType: 'sine', durationSeconds: 0.15, volume: 0.3 },
  { id: 'skiing-stop', category: 'movement', oscillatorFreq: 180, oscillatorType: 'sine', durationSeconds: 0.1, volume: 0.2 },
  { id: 'jet-start', category: 'movement', oscillatorFreq: 330, oscillatorType: 'sawtooth', durationSeconds: 0.12, volume: 0.25 },
  { id: 'jet-stop', category: 'movement', oscillatorFreq: 260, oscillatorType: 'sawtooth', durationSeconds: 0.08, volume: 0.15 },
  { id: 'jump', category: 'movement', oscillatorFreq: 440, oscillatorType: 'triangle', durationSeconds: 0.06, volume: 0.2 },
  { id: 'land', category: 'movement', oscillatorFreq: 120, oscillatorType: 'sine', durationSeconds: 0.1, volume: 0.25 },
  // Phase 94: heavy "thud" played on a hard landing (vertical speed < -25 u/s at touchdown).
  {
    id: 'landing-thud',
    category: 'movement',
    oscillatorFreq: 60,
    oscillatorType: 'sine',
    durationSeconds: 0.32,
    volume: 0.45,
    layers: [
      { oscillatorFreq: 38, oscillatorType: 'sine', durationSeconds: 0.42, volume: 0.32 },
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'lowpass', filterFreq: 320, durationSeconds: 0.18, volume: 0.18 },
    ],
  },
  {
    id: 'spinfusor-fire',
    category: 'combat',
    oscillatorFreq: 180,
    oscillatorType: 'square',
    durationSeconds: 0.15,
    volume: 0.33,
    sampleUrl: '/audio/authored/spinfusor-fire.wav',
    pitchEnd: 120,
    pitchRampSeconds: 0.1,
    layers: [
      { oscillatorFreq: 68, oscillatorType: 'sine', durationSeconds: 0.18, volume: 0.16 },
      { oscillatorFreq: 520, oscillatorType: 'triangle', durationSeconds: 0.055, volume: 0.1, delaySeconds: 0.02 },
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'bandpass', filterFreq: 2000, filterFreqEnd: 400, filterRampSeconds: 0.08, durationSeconds: 0.1, volume: 0.07 },
    ],
  },
  {
    id: 'chaingun-fire',
    category: 'combat',
    oscillatorFreq: 600,
    oscillatorType: 'square',
    durationSeconds: 0.04,
    volume: 0.2,
    sampleUrl: '/audio/authored/chaingun-fire.wav',
    layers: [
      { oscillatorFreq: 920, oscillatorType: 'triangle', durationSeconds: 0.025, volume: 0.08 },
    ],
  },
  // Phase 179: AA turret flak-cannon fire — a punchy low-mid thump with a sharp noise
  // "crack" layered on top so it reads as a heavier, deliberate flak shot rather than
  // the chaingun's fast mechanical rattle.
  {
    id: 'aa-turret-fire',
    category: 'combat',
    oscillatorFreq: 150,
    oscillatorType: 'square',
    durationSeconds: 0.07,
    volume: 0.3,
    layers: [
      { oscillatorFreq: 90, oscillatorType: 'sine', durationSeconds: 0.1, volume: 0.22 },
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'bandpass', filterFreq: 2400, durationSeconds: 0.035, volume: 0.16 },
    ],
  },
  {
    id: 'mortar-fire',
    category: 'combat',
    oscillatorFreq: 100,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.25,
    volume: 0.36,
    sampleUrl: '/audio/authored/mortar-fire.wav',
    layers: [
      { oscillatorFreq: 40, oscillatorType: 'sine', durationSeconds: 0.4, volume: 0.18 },
      { oscillatorFreq: 260, oscillatorType: 'square', durationSeconds: 0.06, volume: 0.1, delaySeconds: 0.03 },
    ],
  },
  { id: 'blaster-fire', category: 'combat', oscillatorFreq: 880, oscillatorType: 'triangle', durationSeconds: 0.06, volume: 0.25 },
  // Phase 88: grenade-launcher signature "thump" — deep 95Hz sine + a tight noise
  // pop layered on top so it reads as a launched grenade rather than a blaster.
  {
    id: 'frag-fire',
    category: 'combat',
    oscillatorFreq: 95,
    oscillatorType: 'sine',
    durationSeconds: 0.16,
    volume: 0.32,
    layers: [
      { oscillatorFreq: 55, oscillatorType: 'triangle', durationSeconds: 0.22, volume: 0.18 },
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'lowpass', filterFreq: 600, durationSeconds: 0.08, volume: 0.12 },
    ],
  },
  { id: 'concussion-fire', category: 'combat', oscillatorFreq: 260, oscillatorType: 'sawtooth', durationSeconds: 0.11, volume: 0.24 },
  // Phase 171-176: procedural fire cues for the new T2 arsenal.
  // Laser rifle — sharp high-frequency zap with a quick downward sweep tail.
  {
    id: 'laser-rifle-fire',
    category: 'combat',
    oscillatorFreq: 1500,
    oscillatorType: 'sine',
    durationSeconds: 0.12,
    volume: 0.26,
    layers: [
      { oscillatorFreq: 2600, oscillatorType: 'triangle', durationSeconds: 0.05, volume: 0.14 },
      { oscillatorFreq: 700, oscillatorType: 'sine', durationSeconds: 0.16, volume: 0.1 },
    ],
  },
  // Plasma gun — warbling mid sawtooth with a filtered noise sizzle.
  {
    id: 'plasma-fire',
    category: 'combat',
    oscillatorFreq: 430,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.14,
    volume: 0.24,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'bandpass', filterFreq: 1200, durationSeconds: 0.1, volume: 0.1 },
    ],
  },
  // Grenade launcher — deeper, hollower thump than the hand-thrown frag.
  {
    id: 'grenade-launcher-fire',
    category: 'combat',
    oscillatorFreq: 110,
    oscillatorType: 'sine',
    durationSeconds: 0.15,
    volume: 0.3,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'lowpass', filterFreq: 800, durationSeconds: 0.07, volume: 0.12 },
    ],
  },
  // ELF projector — continuous electric buzz (short pulse per tick).
  { id: 'elf-fire', category: 'combat', oscillatorFreq: 190, oscillatorType: 'sawtooth', durationSeconds: 0.09, volume: 0.14 },
  // Shocklance — tight high-voltage crack.
  {
    id: 'shocklance-fire',
    category: 'combat',
    oscillatorFreq: 620,
    oscillatorType: 'square',
    durationSeconds: 0.1,
    volume: 0.28,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'highpass', filterFreq: 2200, durationSeconds: 0.06, volume: 0.16 },
    ],
  },
  // Missile launcher — whooshing launch with a low boom underneath.
  {
    id: 'missile-launcher-fire',
    category: 'combat',
    oscillatorFreq: 80,
    oscillatorType: 'sine',
    durationSeconds: 0.22,
    volume: 0.32,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'bandpass', filterFreq: 1600, durationSeconds: 0.18, volume: 0.16 },
      { oscillatorFreq: 150, oscillatorType: 'triangle', durationSeconds: 0.12, volume: 0.12 },
    ],
  },
  {
    id: 'spinfusor-impact',
    category: 'combat',
    oscillatorFreq: 390,
    oscillatorType: 'square',
    durationSeconds: 0.11,
    volume: 0.28,
    sampleUrl: '/audio/authored/spinfusor-impact.wav',
    layers: [
      { oscillatorFreq: 780, oscillatorType: 'triangle', durationSeconds: 0.045, volume: 0.1 },
      { oscillatorFreq: 120, oscillatorType: 'sine', durationSeconds: 0.14, volume: 0.12, delaySeconds: 0.015 },
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterFreq: 980, durationSeconds: 0.06, volume: 0.055 },
    ],
  },
  {
    id: 'mortar-impact',
    category: 'combat',
    oscillatorFreq: 92,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.25,
    volume: 0.32,
    sampleUrl: '/audio/authored/mortar-impact.wav',
    layers: [
      { oscillatorFreq: 46, oscillatorType: 'sine', durationSeconds: 0.5, volume: 0.2 },
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterFreq: 180, durationSeconds: 0.35, volume: 0.11 },
      { oscillatorFreq: 55, oscillatorType: 'sine', durationSeconds: 0.4, volume: 0.14 },
    ],
  },
  {
    id: 'grenade-impact',
    category: 'combat',
    oscillatorFreq: 170,
    oscillatorType: 'triangle',
    durationSeconds: 0.14,
    volume: 0.26,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterFreq: 520, durationSeconds: 0.075, volume: 0.08 },
    ],
  },
  { id: 'hit-confirm', category: 'combat', oscillatorFreq: 520, oscillatorType: 'sine', durationSeconds: 0.08, volume: 0.28 },
  { id: 'blocked-shot', category: 'combat', oscillatorFreq: 240, oscillatorType: 'square', durationSeconds: 0.09, volume: 0.22 },
  { id: 'shield-hit', category: 'combat', oscillatorFreq: 310, oscillatorType: 'triangle', durationSeconds: 0.1, volume: 0.2 },
  { id: 'near-miss', category: 'combat', oscillatorFreq: 720, oscillatorType: 'sine', durationSeconds: 0.045, volume: 0.14 },
  { id: 'flag-grab', category: 'objective', oscillatorFreq: 660, oscillatorType: 'triangle', durationSeconds: 0.2, volume: 0.35 },
  { id: 'flag-return', category: 'objective', oscillatorFreq: 550, oscillatorType: 'triangle', durationSeconds: 0.18, volume: 0.32 },
  {
    id: 'flag-capture',
    category: 'objective',
    oscillatorFreq: 880,
    oscillatorType: 'sine',
    durationSeconds: 0.35,
    volume: 0.4,
    sampleUrl: '/audio/authored/flag-capture.wav',
    layers: [
      { oscillatorFreq: 660, oscillatorType: 'triangle', durationSeconds: 0.2, volume: 0.2 },
      { oscillatorFreq: 1320, oscillatorType: 'sine', durationSeconds: 0.18, volume: 0.16, delaySeconds: 0.08 },
    ],
  },
  {
    id: 'generator-destroy',
    category: 'base',
    oscillatorFreq: 80,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.3,
    volume: 0.38,
    sampleUrl: '/audio/authored/generator-destroy.wav',
    layers: [
      { oscillatorFreq: 40, oscillatorType: 'square', durationSeconds: 0.42, volume: 0.16 },
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterFreq: 110, durationSeconds: 0.48, volume: 0.1 },
    ],
  },
  {
    id: 'generator-repair',
    category: 'base',
    oscillatorFreq: 440,
    oscillatorType: 'sine',
    durationSeconds: 0.12,
    volume: 0.2,
    sampleUrl: '/audio/authored/generator-repair.wav',
    layers: [
      { oscillatorFreq: 660, oscillatorType: 'triangle', durationSeconds: 0.08, volume: 0.08, delaySeconds: 0.04 },
    ],
  },
  { id: 'ui-hover', category: 'announcer', oscillatorFreq: 880, oscillatorType: 'sine', durationSeconds: 0.03, volume: 0.15 },
  { id: 'ui-confirm', category: 'announcer', oscillatorFreq: 1200, oscillatorType: 'triangle', durationSeconds: 0.06, volume: 0.2 },
  { id: 'vehicle-enter', category: 'vehicle', oscillatorFreq: 350, oscillatorType: 'triangle', durationSeconds: 0.1, volume: 0.25 },
  { id: 'vehicle-exit', category: 'vehicle', oscillatorFreq: 280, oscillatorType: 'triangle', durationSeconds: 0.08, volume: 0.2 },
  {
    id: 'vehicle-damage',
    category: 'vehicle',
    oscillatorFreq: 130,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.14,
    volume: 0.26,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterFreq: 340, durationSeconds: 0.08, volume: 0.08 },
    ],
  },
  {
    id: 'vehicle-destroy',
    category: 'vehicle',
    oscillatorFreq: 90,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.25,
    volume: 0.36,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterFreq: 150, durationSeconds: 0.3, volume: 0.14 },
    ],
  },
  { id: 'deployable-place', category: 'base', oscillatorFreq: 460, oscillatorType: 'triangle', durationSeconds: 0.1, volume: 0.22 },
  {
    id: 'generator-hum',
    category: 'base',
    oscillatorFreq: 60,
    oscillatorType: 'sine',
    durationSeconds: 60,
    volume: 0.08,
  },
  {
    id: 'flag-alarm',
    category: 'objective',
    oscillatorFreq: 440,
    oscillatorType: 'square',
    durationSeconds: 0.5,
    volume: 0.32,
    layers: [
      { oscillatorFreq: 660, oscillatorType: 'square', durationSeconds: 0.25, volume: 0.32, delaySeconds: 0.25 },
    ],
  },
  {
    id: 'explosion',
    category: 'combat',
    oscillatorFreq: 80,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.35,
    // Phase 169 — explosion trimmed ~2dB so close-range fights stay readable.
    volume: 0.286,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'lowpass', filterFreq: 2000, filterFreqEnd: 200, filterRampSeconds: 0.3, durationSeconds: 0.35, volume: 0.11 },
      { oscillatorFreq: 50, oscillatorType: 'sine', durationSeconds: 0.3, volume: 0.08 },
    ],
  },
  {
    id: 'footstep-snow',
    category: 'movement',
    oscillatorFreq: 1,
    oscillatorType: 'sine',
    durationSeconds: 0.08,
    volume: 0.12,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'highpass', filterFreq: 2000, durationSeconds: 0.08, volume: 0.12 },
    ],
  },
  {
    id: 'footstep-rock',
    category: 'movement',
    oscillatorFreq: 1,
    oscillatorType: 'sine',
    durationSeconds: 0.1,
    volume: 0.12,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'bandpass', filterFreq: 800, durationSeconds: 0.1, volume: 0.12 },
    ],
  },
  {
    id: 'footstep-metal',
    category: 'movement',
    oscillatorFreq: 1,
    oscillatorType: 'sine',
    durationSeconds: 0.07,
    volume: 0.15,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'bandpass', filterFreq: 1200, durationSeconds: 0.06, volume: 0.1 },
      { oscillatorFreq: 2400, oscillatorType: 'sine', durationSeconds: 0.04, volume: 0.06 },
    ],
  },
  {
    id: 'generator-hum-proximity',
    category: 'base',
    oscillatorFreq: 60,
    oscillatorType: 'sine',
    durationSeconds: 60,
    volume: 0.06,
    layers: [
      { oscillatorFreq: 62, oscillatorType: 'sine', durationSeconds: 60, volume: 0.03 },
      { oscillatorFreq: 120, oscillatorType: 'sine', durationSeconds: 60, volume: 0.015 },
    ],
  },
  {
    id: 'turret-rotate',
    category: 'base',
    oscillatorFreq: 200,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.3,
    volume: 0.12,
    pitchEnd: 400,
    pitchRampSeconds: 0.3,
    layers: [
      { source: 'noise', oscillatorFreq: 1, oscillatorType: 'sine', filterType: 'bandpass', filterFreq: 600, durationSeconds: 0.2, volume: 0.04 },
    ],
  },
  {
    id: 'announce-flag-taken',
    category: 'announcer',
    oscillatorFreq: 523,
    oscillatorType: 'sine',
    durationSeconds: 0.1,
    volume: 0.35,
    layers: [
      { oscillatorFreq: 659, oscillatorType: 'sine', durationSeconds: 0.1, volume: 0.35, delaySeconds: 0.1 },
    ],
  },
  {
    id: 'announce-flag-captured',
    category: 'announcer',
    oscillatorFreq: 523,
    oscillatorType: 'sine',
    durationSeconds: 0.15,
    volume: 0.35,
    layers: [
      { oscillatorFreq: 659, oscillatorType: 'sine', durationSeconds: 0.15, volume: 0.35, delaySeconds: 0.15 },
      { oscillatorFreq: 784, oscillatorType: 'sine', durationSeconds: 0.15, volume: 0.35, delaySeconds: 0.3 },
    ],
  },
  {
    id: 'announce-generator-down',
    category: 'announcer',
    oscillatorFreq: 440,
    oscillatorType: 'sine',
    durationSeconds: 0.4,
    volume: 0.35,
    pitchEnd: 220,
    pitchRampSeconds: 0.4,
    layers: [
      { oscillatorFreq: 443, oscillatorType: 'sine', durationSeconds: 0.4, volume: 0.2, pitchEnd: 221, pitchRampSeconds: 0.4 },
    ],
  },
  {
    id: 'announce-match-start',
    category: 'announcer',
    oscillatorFreq: 392,
    oscillatorType: 'triangle',
    durationSeconds: 0.2,
    volume: 0.35,
    layers: [
      { oscillatorFreq: 523, oscillatorType: 'triangle', durationSeconds: 0.2, volume: 0.35, delaySeconds: 0.2 },
      { oscillatorFreq: 659, oscillatorType: 'triangle', durationSeconds: 0.25, volume: 0.35, delaySeconds: 0.4 },
    ],
  },
  // Phase 89: triumphant rising 3-note chord when our team caps the enemy flag.
  // C5 -> E5 -> G5 (523 / 659 / 784 Hz), staggered, with a sustaining low fifth.
  {
    id: 'sting-friendly-capture',
    category: 'announcer',
    oscillatorFreq: 523,
    oscillatorType: 'triangle',
    durationSeconds: 0.3,
    volume: 0.42,
    layers: [
      { oscillatorFreq: 659, oscillatorType: 'triangle', durationSeconds: 0.3, volume: 0.42, delaySeconds: 0.12 },
      { oscillatorFreq: 784, oscillatorType: 'triangle', durationSeconds: 0.45, volume: 0.42, delaySeconds: 0.24 },
      { oscillatorFreq: 262, oscillatorType: 'sine', durationSeconds: 0.6, volume: 0.22, delaySeconds: 0.0 },
      { oscillatorFreq: 1046, oscillatorType: 'sine', durationSeconds: 0.4, volume: 0.18, delaySeconds: 0.3 },
    ],
  },
  // Phase 89: descending failure sting when the enemy caps our flag.
  // G4 -> Eb4 -> C4 (392 / 311 / 262 Hz), staggered, with a low sustain.
  {
    id: 'sting-enemy-capture',
    category: 'announcer',
    oscillatorFreq: 392,
    oscillatorType: 'sawtooth',
    durationSeconds: 0.32,
    volume: 0.4,
    layers: [
      { oscillatorFreq: 311, oscillatorType: 'sawtooth', durationSeconds: 0.32, volume: 0.4, delaySeconds: 0.16 },
      { oscillatorFreq: 262, oscillatorType: 'triangle', durationSeconds: 0.55, volume: 0.4, delaySeconds: 0.32 },
      { oscillatorFreq: 131, oscillatorType: 'sine', durationSeconds: 0.7, volume: 0.2, delaySeconds: 0.0 },
    ],
  },
];

// Phase 89: helper for the client to play the right flag-capture sting.
export function playFlagCaptureSting(
  isOurTeam: boolean,
  settings?: AudioPlaybackSettings,
): void {
  const cue = cueById.get(isOurTeam ? 'sting-friendly-capture' : 'sting-enemy-capture');
  if (cue) {
    playAudioCue(cue, settings);
  }
}

const cueById = new Map(AUDIO_CUES.map((cue) => [cue.id, cue]));

let audioContext: AudioContext | null = null;
const noiseBufferCache = new Map<string, AudioBuffer>();
const sampleBufferCache = new Map<string, Promise<AudioBuffer | null>>();
const decodedSampleCache = new Map<string, AudioBuffer>();
let audioUnlocked = false;
let audioUnlockListenersInstalled = false;

let masterGainNode: GainNode | null = null;
let underwaterFilterNode: BiquadFilterNode | null = null;
let underwaterActive = false;
let reverbDelayNode: DelayNode | null = null;
let reverbFeedbackNode: GainNode | null = null;
let currentReverbZone: 'outdoor' | 'canyon' | 'indoor' | null = null;

let ambientMusicState: {
  oscillators: OscillatorNode[];
  lfo: OscillatorNode;
  lfoGain: GainNode;
  masterGain: GainNode;
  filter: BiquadFilterNode;
  mode: 'calm' | 'tense';
} | null = null;

let windLoopState: {
  noiseSource: AudioBufferSourceNode;
  bandpassFilter: BiquadFilterNode;
  gainNode: GainNode;
} | null = null;

// Jetpack state for sawtooth + LFO flutter + speed-dependent pitch
let jetpackState: {
  carrier: OscillatorNode;
  lfo: OscillatorNode;
  lfoGain: GainNode;
  outputGain: GainNode;
} | null = null;

// Skiing surface sound state
let skiSurfaceState: {
  noiseSource: AudioBufferSourceNode;
  filter: BiquadFilterNode;
  gainNode: GainNode;
  terrain: string;
} | null = null;

// Spinfusor in-flight hum state
let spinfusorHumState: {
  oscillator: OscillatorNode;
  gain: GainNode;
} | null = null;

// Active projectile source for Doppler effect
let activeProjectileSource: AudioBufferSourceNode | null = null;

function getAudioContext(): AudioContext | null {
  if (audioContext) {
    return audioContext;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }

  try {
    audioContext = new AudioContextClass();
    return audioContext;
  } catch {
    return null;
  }
}

function getMasterOutput(ctx: AudioContext): AudioNode {
  if (!masterGainNode) {
    masterGainNode = ctx.createGain();
    masterGainNode.gain.value = 1;
    rebuildMasterChain(ctx);
  }
  return masterGainNode;
}

function rebuildMasterChain(ctx: AudioContext): void {
  if (!masterGainNode) return;

  try { masterGainNode.disconnect(); } catch { /* ignore */ }
  if (underwaterFilterNode) { try { underwaterFilterNode.disconnect(); } catch { /* ignore */ } }
  if (reverbDelayNode) { try { reverbDelayNode.disconnect(); } catch { /* ignore */ } }
  if (reverbFeedbackNode) { try { reverbFeedbackNode.disconnect(); } catch { /* ignore */ } }
  underwaterFilterNode = null;
  reverbDelayNode = null;
  reverbFeedbackNode = null;

  let finalOutput: AudioNode = ctx.destination;
  if (underwaterActive) {
    underwaterFilterNode = ctx.createBiquadFilter();
    underwaterFilterNode.type = 'lowpass';
    underwaterFilterNode.frequency.value = 800;
    underwaterFilterNode.Q.value = 1.5;
    underwaterFilterNode.connect(ctx.destination);
    finalOutput = underwaterFilterNode;
  }

  masterGainNode.connect(finalOutput);

  if (currentReverbZone && currentReverbZone !== 'outdoor') {
    const delayTime = currentReverbZone === 'canyon' ? 0.1 : 0.05;
    const feedbackAmount = currentReverbZone === 'canyon' ? 0.3 : 0.2;

    reverbDelayNode = ctx.createDelay(1);
    reverbDelayNode.delayTime.value = delayTime;

    reverbFeedbackNode = ctx.createGain();
    reverbFeedbackNode.gain.value = feedbackAmount;

    masterGainNode.connect(reverbDelayNode);
    reverbDelayNode.connect(reverbFeedbackNode);
    reverbFeedbackNode.connect(reverbDelayNode);
    reverbDelayNode.connect(finalOutput);
  }
}

function categoryVolume(cue: AudioCue, settings?: AudioPlaybackSettings) {
  if (settings?.audioMuted) {
    return 0;
  }

  const master = settings?.masterVolume ?? 1;
  const category =
    cue.category === 'combat'
      ? settings?.combatVolume ?? 1
      : cue.category === 'movement'
        ? settings?.movementVolume ?? 1
        : cue.category === 'announcer' || cue.category === 'objective' || cue.category === 'base'
          ? settings?.announcerVolume ?? 1
          : 1;

  return Math.max(0, Math.min(1, master)) * Math.max(0, Math.min(1, category));
}

export function resolveCueVolume(cue: AudioCue, settings?: AudioPlaybackSettings): number {
  return Math.max(0, Math.min(1, cue.volume * categoryVolume(cue, settings)));
}

export function getAudioCueLayers(cue: AudioCue): AudioCueLayer[] {
  return [
    {
      oscillatorFreq: cue.oscillatorFreq,
      oscillatorType: cue.oscillatorType,
      durationSeconds: cue.durationSeconds,
      volume: cue.volume,
      delaySeconds: 0,
      source: 'oscillator',
      pitchEnd: cue.pitchEnd,
      pitchRampSeconds: cue.pitchRampSeconds,
    },
    ...(cue.layers ?? []),
  ];
}

// ─── Layered Explosion ────────────────────────────────────────────────────────

function playLayeredExplosion(ctx: AudioContext, output: AudioNode, volumeScale: number): void {
  const now = ctx.currentTime;

  // Layer 1: Sub-bass rumble
  const subFilter = ctx.createBiquadFilter();
  subFilter.type = 'lowpass';
  subFilter.frequency.value = 80;
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0.8 * volumeScale, now);
  subGain.gain.linearRampToValueAtTime(0, now + 1.8);
  const subOsc = ctx.createOscillator();
  subOsc.type = 'sine';
  subOsc.frequency.value = 40;
  subOsc.connect(subFilter);
  subFilter.connect(subGain);
  subGain.connect(output);
  subOsc.start(now);
  subOsc.stop(now + 1.8);
  subOsc.onended = () => { try { subOsc.disconnect(); subFilter.disconnect(); subGain.disconnect(); } catch { /* ignore */ } };

  // Layer 2: Crack — white noise through bandpass
  const noiseBuffer = createNoiseBuffer(ctx, 0.25);
  const crackSource = ctx.createBufferSource();
  crackSource.buffer = noiseBuffer;
  const crackFilter = ctx.createBiquadFilter();
  crackFilter.type = 'bandpass';
  crackFilter.frequency.value = 2400;
  crackFilter.Q.value = 0.5;
  const crackGain = ctx.createGain();
  crackGain.gain.setValueAtTime(1.0 * volumeScale, now);
  crackGain.gain.linearRampToValueAtTime(0, now + 0.25);
  crackSource.connect(crackFilter);
  crackFilter.connect(crackGain);
  crackGain.connect(output);
  crackSource.start(now);
  crackSource.stop(now + 0.25);
  crackSource.onended = () => { try { crackSource.disconnect(); crackFilter.disconnect(); crackGain.disconnect(); } catch { /* ignore */ } };

  // Layer 3: Debris rattle — 12 short oscillators staggered 0–200ms
  for (let i = 0; i < 12; i++) {
    const delay = (i / 12) * 0.2;
    const freq = 150 + Math.random() * 250;
    const rattleGain = ctx.createGain();
    rattleGain.gain.setValueAtTime(0.12 * volumeScale, now + delay);
    rattleGain.gain.linearRampToValueAtTime(0, now + delay + 0.03);
    const rattleOsc = ctx.createOscillator();
    rattleOsc.type = 'sine';
    rattleOsc.frequency.value = freq;
    rattleOsc.connect(rattleGain);
    rattleGain.connect(output);
    rattleOsc.start(now + delay);
    rattleOsc.stop(now + delay + 0.03);
    rattleOsc.onended = () => { try { rattleOsc.disconnect(); rattleGain.disconnect(); } catch { /* ignore */ } };
  }
}

// ─── Spinfusor Sound ─────────────────────────────────────────────────────────

export function playSpinfusorThrow(settings?: AudioPlaybackSettings): void {
  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const output = getMasterOutput(ctx);
    const now = ctx.currentTime;
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.combatVolume ?? 1)) * 0.5;

    // Rising frequency sweep 400→1800 Hz over 0.4s + volume swell
    const throwGain = ctx.createGain();
    throwGain.gain.setValueAtTime(0, now);
    throwGain.gain.linearRampToValueAtTime(vol, now + 0.2);
    throwGain.gain.linearRampToValueAtTime(0, now + 0.4);
    const throwOsc = ctx.createOscillator();
    throwOsc.type = 'sine';
    throwOsc.frequency.setValueAtTime(400, now);
    throwOsc.frequency.linearRampToValueAtTime(1800, now + 0.4);
    throwOsc.connect(throwGain);
    throwGain.connect(output);
    throwOsc.start(now);
    throwOsc.stop(now + 0.4);
    throwOsc.onended = () => { try { throwOsc.disconnect(); throwGain.disconnect(); } catch { /* ignore */ } };

    // Start in-flight hum (220 Hz looping)
    stopSpinfusorHum();
    const humGain = ctx.createGain();
    humGain.gain.value = Math.min(vol * 0.35, 0.15);
    const humOsc = ctx.createOscillator();
    humOsc.type = 'sine';
    humOsc.frequency.value = 220;
    humOsc.connect(humGain);
    humGain.connect(output);
    humOsc.start(now + 0.35);
    spinfusorHumState = { oscillator: humOsc, gain: humGain };
  } catch { /* ignore */ }
}

export function stopSpinfusorHum(): void {
  if (!spinfusorHumState) return;
  try {
    spinfusorHumState.oscillator.stop();
    spinfusorHumState.oscillator.disconnect();
    spinfusorHumState.gain.disconnect();
  } catch { /* ignore */ }
  spinfusorHumState = null;
}

export function playSpinfusorImpact(settings?: AudioPlaybackSettings): void {
  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    stopSpinfusorHum();
    const output = getMasterOutput(ctx);
    const now = ctx.currentTime;
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.combatVolume ?? 1)) * 0.7;

    // Heavy low thump 60 Hz
    const thumpGain = ctx.createGain();
    thumpGain.gain.setValueAtTime(0.9 * vol, now);
    thumpGain.gain.linearRampToValueAtTime(0, now + 0.4);
    const thumpOsc = ctx.createOscillator();
    thumpOsc.type = 'sine';
    thumpOsc.frequency.value = 60;
    thumpOsc.connect(thumpGain);
    thumpGain.connect(output);
    thumpOsc.start(now);
    thumpOsc.stop(now + 0.4);
    thumpOsc.onended = () => { try { thumpOsc.disconnect(); thumpGain.disconnect(); } catch { /* ignore */ } };

    // Crack layer — noise burst
    const noiseBuffer = createNoiseBuffer(ctx, 0.12);
    const crackSrc = ctx.createBufferSource();
    crackSrc.buffer = noiseBuffer;
    const crackFilt = ctx.createBiquadFilter();
    crackFilt.type = 'bandpass';
    crackFilt.frequency.value = 2400;
    crackFilt.Q.value = 0.6;
    const crackGain = ctx.createGain();
    crackGain.gain.setValueAtTime(vol, now);
    crackGain.gain.linearRampToValueAtTime(0, now + 0.12);
    crackSrc.connect(crackFilt);
    crackFilt.connect(crackGain);
    crackGain.connect(output);
    crackSrc.start(now);
    crackSrc.stop(now + 0.12);
    crackSrc.onended = () => { try { crackSrc.disconnect(); crackFilt.disconnect(); crackGain.disconnect(); } catch { /* ignore */ } };
  } catch { /* ignore */ }
}

// ─── Chaingun FM Sound ────────────────────────────────────────────────────────

export function playChaingunShot(windUpRatio: number, settings?: AudioPlaybackSettings): void {
  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const output = getMasterOutput(ctx);
    const now = ctx.currentTime;
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.combatVolume ?? 1)) * 0.3;
    const ratio = Math.max(0, Math.min(1, windUpRatio));

    // FM synthesis: carrier 220Hz, modulator 440Hz, modulation depth 0→300 per wind-up
    const modDepth = ratio * 300;
    const modGain = ctx.createGain();
    modGain.gain.value = modDepth;
    const modOsc = ctx.createOscillator();
    modOsc.type = 'sine';
    modOsc.frequency.value = 440;
    modOsc.connect(modGain);
    modGain.connect(/* carrier freq param via AudioParam trick */ modOsc.frequency); // self-FM for tone

    const carrierGain = ctx.createGain();
    carrierGain.gain.setValueAtTime(vol, now);
    carrierGain.gain.linearRampToValueAtTime(0, now + 0.04);
    const carrierOsc = ctx.createOscillator();
    carrierOsc.type = 'sine';
    carrierOsc.frequency.value = 220;
    modGain.connect(carrierOsc.frequency);
    carrierOsc.connect(carrierGain);
    carrierGain.connect(output);

    modOsc.start(now);
    modOsc.stop(now + 0.04);
    carrierOsc.start(now);
    carrierOsc.stop(now + 0.04);
    carrierOsc.onended = () => {
      try { carrierOsc.disconnect(); carrierGain.disconnect(); modOsc.disconnect(); modGain.disconnect(); } catch { /* ignore */ }
    };
  } catch { /* ignore */ }
}

// ─── Mortar Sound ─────────────────────────────────────────────────────────────

export function playMortarThrow(settings?: AudioPlaybackSettings): void {
  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const output = getMasterOutput(ctx);
    const now = ctx.currentTime;
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.combatVolume ?? 1)) * 0.5;

    // Bass whomp — 80 Hz triangle, 0.3s envelope
    const whompGain = ctx.createGain();
    whompGain.gain.setValueAtTime(0, now);
    whompGain.gain.linearRampToValueAtTime(vol * 0.9, now + 0.05);
    whompGain.gain.linearRampToValueAtTime(0, now + 0.3);
    const whompOsc = ctx.createOscillator();
    whompOsc.type = 'triangle';
    whompOsc.frequency.value = 80;
    whompOsc.connect(whompGain);
    whompGain.connect(output);
    whompOsc.start(now);
    whompOsc.stop(now + 0.3);
    whompOsc.onended = () => { try { whompOsc.disconnect(); whompGain.disconnect(); } catch { /* ignore */ } };

    // Low whistle 180→120 Hz over 0.8s
    const whistleGain = ctx.createGain();
    whistleGain.gain.setValueAtTime(vol * 0.4, now + 0.1);
    whistleGain.gain.linearRampToValueAtTime(0, now + 0.9);
    const whistleOsc = ctx.createOscillator();
    whistleOsc.type = 'sine';
    whistleOsc.frequency.setValueAtTime(180, now + 0.1);
    whistleOsc.frequency.linearRampToValueAtTime(120, now + 0.9);
    whistleOsc.connect(whistleGain);
    whistleGain.connect(output);
    whistleOsc.start(now + 0.1);
    whistleOsc.stop(now + 0.9);
    whistleOsc.onended = () => { try { whistleOsc.disconnect(); whistleGain.disconnect(); } catch { /* ignore */ } };
  } catch { /* ignore */ }
}

export function playMortarImpact(settings?: AudioPlaybackSettings): void {
  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const output = getMasterOutput(ctx);
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.combatVolume ?? 1)) * 0.8;
    playLayeredExplosion(ctx, output, vol);

    // Additional lower sub-bass 30 Hz
    const now = ctx.currentTime;
    const subGain = ctx.createGain();
    const subFilt = ctx.createBiquadFilter();
    subFilt.type = 'lowpass';
    subFilt.frequency.value = 60;
    subGain.gain.setValueAtTime(0.7 * vol, now);
    subGain.gain.linearRampToValueAtTime(0, now + 2.2);
    const subOsc = ctx.createOscillator();
    subOsc.type = 'sine';
    subOsc.frequency.value = 30;
    subOsc.connect(subFilt);
    subFilt.connect(subGain);
    subGain.connect(output);
    subOsc.start(now);
    subOsc.stop(now + 2.2);
    subOsc.onended = () => { try { subOsc.disconnect(); subFilt.disconnect(); subGain.disconnect(); } catch { /* ignore */ } };
  } catch { /* ignore */ }
}

// ─── Jetpack Sound ────────────────────────────────────────────────────────────

export function startJetpackSound(velocity?: number, settings?: AudioPlaybackSettings): void {
  if (jetpackState) return;
  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const output = getMasterOutput(ctx);
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.movementVolume ?? 1)) * 0.3;
    const speed = Math.max(0, Math.min(1, (velocity ?? 0) / 100));
    const pitch = 180 + speed * 200; // 180→380 Hz

    // Sawtooth carrier at pitch Hz
    const outputGain = ctx.createGain();
    outputGain.gain.value = vol;

    // LFO for flutter at 12 Hz
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = vol * 0.4;
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 12;
    lfo.connect(lfoGain);
    lfoGain.connect(outputGain.gain);

    const carrier = ctx.createOscillator();
    carrier.type = 'sawtooth';
    carrier.frequency.value = pitch;
    carrier.connect(outputGain);
    outputGain.connect(output);

    carrier.start();
    lfo.start();

    jetpackState = { carrier, lfo, lfoGain, outputGain };
  } catch { /* ignore */ }
}

export function updateJetpackSound(velocity?: number, settings?: AudioPlaybackSettings): void {
  if (!jetpackState) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const speed = Math.max(0, Math.min(1, (velocity ?? 0) / 100));
    const pitch = 180 + speed * 200;
    jetpackState.carrier.frequency.setTargetAtTime(pitch, ctx.currentTime, 0.05);
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.movementVolume ?? 1)) * 0.3;
    jetpackState.outputGain.gain.setTargetAtTime(vol, ctx.currentTime, 0.05);
    jetpackState.lfoGain.gain.setTargetAtTime(vol * 0.4, ctx.currentTime, 0.05);
  } catch { /* ignore */ }
}

export function stopJetpackSound(): void {
  if (!jetpackState) return;
  try {
    jetpackState.carrier.stop();
    jetpackState.lfo.stop();
    jetpackState.carrier.disconnect();
    jetpackState.lfo.disconnect();
    jetpackState.lfoGain.disconnect();
    jetpackState.outputGain.disconnect();
  } catch { /* ignore */ }
  jetpackState = null;
}

// ─── Skiing Surface Sound ─────────────────────────────────────────────────────

export function startSkiingSound(terrain: 'snow' | 'rock' | 'metal', speed: number, settings?: AudioPlaybackSettings): void {
  if (skiSurfaceState && skiSurfaceState.terrain === terrain) {
    updateSkiingSound(terrain, speed, settings);
    return;
  }
  stopSkiingSound();
  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const output = getMasterOutput(ctx);
    const speedNorm = Math.max(0, Math.min(1, speed / 100));
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.movementVolume ?? 1)) * 0.168 * speedNorm;

    const gainNode = ctx.createGain();
    gainNode.gain.value = vol;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    if (terrain === 'snow') {
      filter.frequency.value = 500;
      filter.Q.value = 1.2;
    } else if (terrain === 'rock') {
      filter.frequency.value = 250;
      filter.Q.value = 1.5;
    } else {
      // metal
      filter.frequency.value = 900;
      filter.Q.value = 2.0;
    }

    const bufferSize = Math.ceil(ctx.sampleRate * 2);
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const channelData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      channelData[i] = Math.random() * 2 - 1;
    }

    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;
    noiseSource.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(output);
    noiseSource.start();

    skiSurfaceState = { noiseSource, filter, gainNode, terrain };
  } catch { /* ignore */ }
}

export function updateSkiingSound(terrain: 'snow' | 'rock' | 'metal', speed: number, settings?: AudioPlaybackSettings): void {
  if (!skiSurfaceState) {
    startSkiingSound(terrain, speed, settings);
    return;
  }
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const speedNorm = Math.max(0, Math.min(1, speed / 100));
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.movementVolume ?? 1)) * 0.168 * speedNorm;
    skiSurfaceState.gainNode.gain.setTargetAtTime(vol, ctx.currentTime, 0.05);
  } catch { /* ignore */ }
}

export function stopSkiingSound(): void {
  if (!skiSurfaceState) return;
  try {
    skiSurfaceState.noiseSource.stop();
    skiSurfaceState.noiseSource.disconnect();
    skiSurfaceState.filter.disconnect();
    skiSurfaceState.gainNode.disconnect();
  } catch { /* ignore */ }
  skiSurfaceState = null;
}

// ─── Announcer Voice ──────────────────────────────────────────────────────────

// Phase 169 — duck ambient music by ~6dB while the announcer is speaking,
// then restore over a 1s release. Uses linear ramps for predictable timing.
export function duckAmbientMusic(durationSeconds: number = 1): void {
  if (!ambientMusicState) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const param = ambientMusicState.masterGain.gain;
    const now = ctx.currentTime;
    const currentValue = param.value;
    // -6dB ≈ ×0.5.
    const target = currentValue * 0.5;
    param.cancelScheduledValues(now);
    param.setValueAtTime(currentValue, now);
    param.linearRampToValueAtTime(target, now + 0.08);
    param.linearRampToValueAtTime(currentValue, now + Math.max(0.5, durationSeconds));
  } catch {
    // ignore
  }
}

export function playAnnouncement(event: AnnouncerEvent): void {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  const text = ANNOUNCER_LINES[event];
  if (!text) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = 0.9;
  utterance.pitch = 1.15;
  utterance.volume = 0.9;
  // Phase 169 — duck music for ~1s while the line plays.
  duckAmbientMusic(1);
  window.speechSynthesis.speak(utterance);
}

// ─── Ambient Music ────────────────────────────────────────────────────────────

export function startAmbientMusic(mode: 'calm' | 'tense'): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ambientMusicState) {
    // Crossfade to new mode over 2s
    if (ambientMusicState.mode === mode) return;
    try {
      const now = ctx.currentTime;
      const isCalm = mode === 'calm';
      const targetCutoff = isCalm ? 180 : 280;
      const targetGainMin = isCalm ? 0.04 : 0.08;
      const targetGainMax = isCalm ? 0.14 : 0.18;
      const targetLfoFreq = isCalm ? 0.08 : 0.25;
      ambientMusicState.filter.frequency.linearRampToValueAtTime(targetCutoff, now + 2);
      ambientMusicState.masterGain.gain.linearRampToValueAtTime(targetGainMin, now + 1);
      ambientMusicState.masterGain.gain.linearRampToValueAtTime((targetGainMin + targetGainMax) / 2, now + 2);
      ambientMusicState.lfo.frequency.linearRampToValueAtTime(targetLfoFreq, now + 2);
      ambientMusicState.lfoGain.gain.linearRampToValueAtTime((targetGainMax - targetGainMin) / 2, now + 2);
      ambientMusicState.mode = mode;
    } catch { /* ignore */ }
    return;
  }

  try {
    const isCalm = mode === 'calm';
    const lfoFreq = isCalm ? 0.08 : 0.25;
    const cutoff = isCalm ? 180 : 280;
    const gainMin = isCalm ? 0.04 : 0.08;
    const gainMax = isCalm ? 0.14 : 0.18;
    const gainMid = (gainMin + gainMax) / 2;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;

    const masterGain = ctx.createGain();
    masterGain.gain.value = gainMid;

    const lfoGain = ctx.createGain();
    lfoGain.gain.value = (gainMax - gainMin) / 2;

    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = lfoFreq;
    lfo.connect(lfoGain);
    lfoGain.connect(masterGain.gain);

    // 3 detuned square oscillators
    const freqs = [55.0, 55.15, 54.85];
    const oscillators = freqs.map((freq) => {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = freq;
      osc.connect(filter);
      osc.start();
      return osc;
    });

    filter.connect(masterGain);
    lfo.start();
    masterGain.connect(getMasterOutput(ctx));

    ambientMusicState = { oscillators, lfo, lfoGain, masterGain, filter, mode };
  } catch { /* ignore */ }
}

export function stopAmbientMusic(): void {
  if (!ambientMusicState) return;
  try {
    for (const osc of ambientMusicState.oscillators) {
      osc.stop();
      osc.disconnect();
    }
    ambientMusicState.lfo.stop();
    ambientMusicState.lfo.disconnect();
    ambientMusicState.lfoGain.disconnect();
    ambientMusicState.filter.disconnect();
    ambientMusicState.masterGain.disconnect();
  } catch { /* ignore */ }
  ambientMusicState = null;
}

// ─── Doppler Effect ───────────────────────────────────────────────────────────

export function updateProjectileDoppler(
  projectilePos: { x: number; y: number; z: number },
  playerPos: { x: number; y: number; z: number },
  projectileVelocity: { x: number; y: number; z: number },
): void {
  if (!activeProjectileSource) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const dx = projectilePos.x - playerPos.x;
    const dy = projectilePos.y - playerPos.y;
    const dz = projectilePos.z - playerPos.z;
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (distance > 12) return;

    // Dot product of velocity with relative position to determine passing
    const dot = dx * projectileVelocity.x + dy * projectileVelocity.y + dz * projectileVelocity.z;
    // Negative dot means projectile has passed the player (was in front, now behind)
    if (dot < 0) {
      const now = ctx.currentTime;
      activeProjectileSource.playbackRate.setValueAtTime(1.3, now);
      activeProjectileSource.playbackRate.linearRampToValueAtTime(0.7, now + 0.25);
    }
  } catch { /* ignore */ }
}

export function registerProjectileSource(source: AudioBufferSourceNode): void {
  activeProjectileSource = source;
}

export function clearProjectileSource(): void {
  activeProjectileSource = null;
}

// ─── Explosion public export ──────────────────────────────────────────────────

export function playExplosion(settings?: AudioPlaybackSettings): void {
  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const output = getMasterOutput(ctx);
    const vol = (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.combatVolume ?? 1)) * 0.8;
    playLayeredExplosion(ctx, output, vol);
  } catch { /* ignore */ }
}

// Phase 90: Distance-attenuated explosion. Full volume within 80u of the local
// player, fades linearly to silence by 400u. Skipped entirely beyond 400u.
export function playExplosionAtDistance(
  sourcePos: { x: number; y: number; z: number },
  listenerPos: { x: number; y: number; z: number },
  settings?: AudioPlaybackSettings,
): void {
  const dx = sourcePos.x - listenerPos.x;
  const dy = sourcePos.y - listenerPos.y;
  const dz = sourcePos.z - listenerPos.z;
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const FULL_VOLUME_RADIUS = 80;
  const SILENCE_RADIUS = 400;
  if (distance >= SILENCE_RADIUS) return;
  const falloff =
    distance <= FULL_VOLUME_RADIUS
      ? 1
      : 1 - (distance - FULL_VOLUME_RADIUS) / (SILENCE_RADIUS - FULL_VOLUME_RADIUS);
  if (falloff <= 0.001) return;

  if (!isAudioPlaybackUnlocked()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const output = getMasterOutput(ctx);
    const baseVol =
      (settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1) * (settings?.combatVolume ?? 1)) * 0.8;
    playLayeredExplosion(ctx, output, baseVol * falloff);
  } catch { /* ignore */ }
}

export function playAudioCue(cue: AudioCue, settings?: AudioPlaybackSettings): void {
  const categoryGain = categoryVolume(cue, settings);
  if (categoryGain <= 0.001) {
    return;
  }

  if (!isAudioPlaybackUnlocked()) {
    return;
  }

  const ctx = getAudioContext();
  if (!ctx) {
    return;
  }

  try {
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const output = getMasterOutput(ctx);

    // Use layered explosion for explosion cue
    if (cue.id === 'explosion') {
      playLayeredExplosion(ctx, output, categoryGain);
      return;
    }

    if (cue.sampleUrl && playSampleCue(ctx, cue, categoryGain, output)) {
      return;
    }

    for (const layer of getAudioCueLayers(cue)) {
      const volume = Math.max(0, Math.min(1, layer.volume * categoryGain));
      if (volume <= 0.001) {
        continue;
      }

      const gain = ctx.createGain();
      const startTime = ctx.currentTime + Math.max(0, layer.delaySeconds ?? 0);
      const durationSeconds = Math.max(0.01, layer.durationSeconds);

      gain.gain.setValueAtTime(volume, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + durationSeconds);

      if (layer.source === 'noise') {
        const source = ctx.createBufferSource();
        source.buffer = createNoiseBuffer(ctx, durationSeconds);
        if (typeof ctx.createBiquadFilter === 'function') {
          const noiseFilter = ctx.createBiquadFilter();
          noiseFilter.type = layer.filterType ?? 'bandpass';
          noiseFilter.frequency.setValueAtTime(layer.filterFreq ?? 420, startTime);
          if (layer.filterFreqEnd != null && layer.filterRampSeconds != null && layer.filterRampSeconds > 0) {
            noiseFilter.frequency.exponentialRampToValueAtTime(Math.max(1, layer.filterFreqEnd), startTime + layer.filterRampSeconds);
          }
          source.connect(noiseFilter);
          noiseFilter.connect(gain);
        } else {
          source.connect(gain);
        }
        gain.connect(output);
        source.start(startTime);
        source.stop(startTime + durationSeconds);
      } else {
        const oscillator = ctx.createOscillator();
        oscillator.type = layer.oscillatorType;
        oscillator.frequency.setValueAtTime(layer.oscillatorFreq, startTime);
        if (layer.pitchEnd != null && layer.pitchEnd !== layer.oscillatorFreq && layer.pitchRampSeconds != null && layer.pitchRampSeconds > 0) {
          oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, layer.pitchEnd), startTime + layer.pitchRampSeconds);
        }
        oscillator.connect(gain);
        gain.connect(output);
        oscillator.start(startTime);
        oscillator.stop(startTime + durationSeconds);
      }
    }
  } catch {
    // Gracefully handle audio failures
  }
}

export function playPositionalAudioCue(
  cue: AudioCue,
  sourcePosition: { x: number; y: number; z: number },
  listenerPosition: { x: number; y: number; z: number },
  settings?: AudioPlaybackSettings,
): void {
  const dx = sourcePosition.x - listenerPosition.x;
  const dy = sourcePosition.y - listenerPosition.y;
  const dz = sourcePosition.z - listenerPosition.z;
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

  if (distance > 300) {
    return;
  }

  const categoryGain = categoryVolume(cue, settings);
  if (categoryGain <= 0.001) {
    return;
  }

  if (!isAudioPlaybackUnlocked()) {
    return;
  }

  const ctx = getAudioContext();
  if (!ctx) {
    return;
  }

  try {
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const currentTime = ctx.currentTime;
    ctx.listener.positionX.setValueAtTime(listenerPosition.x, currentTime);
    ctx.listener.positionY.setValueAtTime(listenerPosition.y, currentTime);
    ctx.listener.positionZ.setValueAtTime(listenerPosition.z, currentTime);

    const panner = ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 20;
    panner.maxDistance = 300;
    panner.rolloffFactor = 1.2;
    panner.positionX.value = sourcePosition.x;
    panner.positionY.value = sourcePosition.y;
    panner.positionZ.value = sourcePosition.z;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    const t = Math.min(1, distance / 200);
    const cutoff = 20000 - t * 18000;
    filter.frequency.value = cutoff;

    const output = getMasterOutput(ctx);

    if (cue.sampleUrl && playSampleCuePositional(ctx, cue, categoryGain, filter, panner, output)) {
      return;
    }

    for (const layer of getAudioCueLayers(cue)) {
      const volume = Math.max(0, Math.min(1, layer.volume * categoryGain));
      if (volume <= 0.001) {
        continue;
      }

      const gain = ctx.createGain();
      const startTime = ctx.currentTime + Math.max(0, layer.delaySeconds ?? 0);
      const durationSeconds = Math.max(0.01, layer.durationSeconds);

      gain.gain.setValueAtTime(volume, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + durationSeconds);

      if (layer.source === 'noise') {
        const source = ctx.createBufferSource();
        source.buffer = createNoiseBuffer(ctx, durationSeconds);
        if (typeof ctx.createBiquadFilter === 'function') {
          const noiseFilter = ctx.createBiquadFilter();
          noiseFilter.type = layer.filterType ?? 'bandpass';
          noiseFilter.frequency.setValueAtTime(layer.filterFreq ?? 420, startTime);
          if (layer.filterFreqEnd != null && layer.filterRampSeconds != null && layer.filterRampSeconds > 0) {
            noiseFilter.frequency.exponentialRampToValueAtTime(Math.max(1, layer.filterFreqEnd), startTime + layer.filterRampSeconds);
          }
          source.connect(noiseFilter);
          noiseFilter.connect(gain);
        } else {
          source.connect(gain);
        }
        gain.connect(filter);
        filter.connect(panner);
        panner.connect(output);
        source.start(startTime);
        source.stop(startTime + durationSeconds);
      } else {
        const oscillator = ctx.createOscillator();
        oscillator.type = layer.oscillatorType;
        oscillator.frequency.setValueAtTime(layer.oscillatorFreq, startTime);
        if (layer.pitchEnd != null && layer.pitchEnd !== layer.oscillatorFreq && layer.pitchRampSeconds != null && layer.pitchRampSeconds > 0) {
          oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, layer.pitchEnd), startTime + layer.pitchRampSeconds);
        }
        oscillator.connect(gain);
        gain.connect(filter);
        filter.connect(panner);
        panner.connect(output);
        oscillator.start(startTime);
        oscillator.stop(startTime + durationSeconds);
      }
    }
  } catch {
    // Gracefully handle audio failures
  }
}

function playSampleCue(ctx: AudioContext, cue: AudioCue, categoryGain: number, output: AudioNode): boolean {
  if (!cue.sampleUrl) {
    return false;
  }

  const decoded = decodedSampleCache.get(cue.sampleUrl);
  if (!decoded) {
    void getAudioSampleBuffer(ctx, cue.sampleUrl);
    return false;
  }

  const source = ctx.createBufferSource();
  const gain = ctx.createGain();
  source.buffer = decoded;
  gain.gain.setValueAtTime(Math.max(0, Math.min(1, cue.volume * categoryGain)), ctx.currentTime);
  source.connect(gain);
  gain.connect(output);
  source.start();
  return true;
}

function playSampleCuePositional(
  ctx: AudioContext,
  cue: AudioCue,
  categoryGain: number,
  filter: BiquadFilterNode,
  panner: PannerNode,
  output: AudioNode,
): boolean {
  if (!cue.sampleUrl) {
    return false;
  }

  const decoded = decodedSampleCache.get(cue.sampleUrl);
  if (!decoded) {
    void getAudioSampleBuffer(ctx, cue.sampleUrl);
    return false;
  }

  const source = ctx.createBufferSource();
  const gain = ctx.createGain();
  source.buffer = decoded;
  gain.gain.setValueAtTime(Math.max(0, Math.min(1, cue.volume * categoryGain)), ctx.currentTime);
  source.connect(gain);
  gain.connect(filter);
  filter.connect(panner);
  panner.connect(output);
  source.start();
  return true;
}

function getAudioSampleBuffer(ctx: AudioContext, url: string): Promise<AudioBuffer | null> {
  const cached = sampleBufferCache.get(url);
  if (cached) {
    return cached;
  }

  if (typeof fetch !== 'function') {
    return Promise.resolve(null);
  }

  const promise = fetch(url)
    .then((response) => (response.ok ? response.arrayBuffer() : null))
    .then((buffer) => (buffer ? ctx.decodeAudioData(buffer.slice(0)) : null))
    .then((decoded) => {
      if (decoded) {
        decodedSampleCache.set(url, decoded);
      }
      return decoded;
    })
    .catch(() => null);
  sampleBufferCache.set(url, promise);
  return promise;
}

function isAudioPlaybackUnlocked(): boolean {
  if (audioUnlocked) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  installAudioUnlockListeners();
  return false;
}

function installAudioUnlockListeners(): void {
  if (audioUnlockListenersInstalled || typeof window === 'undefined') {
    return;
  }

  audioUnlockListenersInstalled = true;
  const unlock = () => {
    audioUnlocked = true;
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    if (audioContext?.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }
  };

  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}

function createNoiseBuffer(ctx: AudioContext, durationSeconds: number): AudioBuffer {
  const frameCount = Math.max(1, Math.ceil(ctx.sampleRate * durationSeconds));
  const cacheKey = `${ctx.sampleRate}:${frameCount}`;
  const cached = noiseBufferCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const buffer = ctx.createBuffer(1, frameCount, ctx.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let index = 0; index < frameCount; index += 1) {
    channel[index] = Math.random() * 2 - 1;
  }
  if (noiseBufferCache.size > 24) {
    noiseBufferCache.clear();
  }
  noiseBufferCache.set(cacheKey, buffer);
  return buffer;
}

const activeLoops = new Map<string, { oscillator: OscillatorNode; gain: GainNode }>();

export function startAudioLoop(id: string, freq: number, type: OscillatorType, volume: number, settings?: AudioPlaybackSettings): void {
  if (activeLoops.has(id)) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const master = settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1);
    const mov = settings?.movementVolume ?? 1;
    const gain = ctx.createGain();
    gain.gain.value = Math.max(0, Math.min(1, volume * master * mov));
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(getMasterOutput(ctx));
    osc.start();
    activeLoops.set(id, { oscillator: osc, gain });
  } catch {
    // ignore
  }
}

export function stopAudioLoop(id: string): void {
  const loop = activeLoops.get(id);
  if (!loop) return;
  try {
    loop.oscillator.stop();
    loop.oscillator.disconnect();
    loop.gain.disconnect();
  } catch {
    // ignore
  }
  activeLoops.delete(id);
}

export function updateAudioLoopVolume(id: string, volume: number, settings?: AudioPlaybackSettings): void {
  const loop = activeLoops.get(id);
  if (!loop) return;
  const master = settings?.audioMuted ? 0 : (settings?.masterVolume ?? 1);
  const mov = settings?.movementVolume ?? 1;
  loop.gain.gain.setValueAtTime(Math.max(0, Math.min(1, volume * master * mov)), (getAudioContext() ?? { currentTime: 0 }).currentTime);
}

// Phase 93: update the carrier frequency of a running loop (e.g. jet pitch rises with energy use).
export function updateAudioLoopFrequency(id: string, freq: number): void {
  const loop = activeLoops.get(id);
  if (!loop) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    loop.oscillator.frequency.setTargetAtTime(Math.max(20, freq), ctx.currentTime, 0.05);
  } catch {
    // ignore
  }
}

export function getAudioCue(id: string): AudioCue | undefined {
  return cueById.get(id);
}

export function getAllAudioCues(): AudioCue[] {
  return AUDIO_CUES;
}

export function updateWindAltitude(normalizedAltitude: number): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const altitude = Math.max(0, Math.min(1, normalizedAltitude));

  if (!windLoopState) {
    try {
      const gainNode = ctx.createGain();
      const bandpassFilter = ctx.createBiquadFilter();
      bandpassFilter.type = 'bandpass';
      bandpassFilter.frequency.value = 200 + altitude * 800;
      bandpassFilter.Q.value = 0.5;

      const bufferSize = Math.ceil(ctx.sampleRate * 2);
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const channelData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        channelData[i] = Math.random() * 2 - 1;
      }

      const noiseSource = ctx.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = true;
      noiseSource.connect(bandpassFilter);
      bandpassFilter.connect(gainNode);
      gainNode.connect(getMasterOutput(ctx));
      gainNode.gain.value = 0.02 + altitude * 0.06;
      noiseSource.start();

      windLoopState = { noiseSource, bandpassFilter, gainNode };
    } catch {
      // ignore
    }
    return;
  }

  try {
    const filterFreq = 200 + altitude * 800;
    const volume = 0.02 + altitude * 0.06;
    windLoopState.bandpassFilter.frequency.setTargetAtTime(filterFreq, ctx.currentTime, 0.1);
    windLoopState.gainNode.gain.setTargetAtTime(volume, ctx.currentTime, 0.1);
  } catch {
    // ignore
  }
}

export function setUnderwaterAudio(active: boolean): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  underwaterActive = active;
  if (masterGainNode) {
    rebuildMasterChain(ctx);
  }
}

export function setReverbZone(zone: 'outdoor' | 'canyon' | 'indoor'): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  currentReverbZone = zone;
  if (masterGainNode) {
    rebuildMasterChain(ctx);
  }
}

export function getWeaponFireCueId(weaponId?: string | null): string | null {
  switch ((weaponId ?? '').toLowerCase()) {
    case 'spinfusor':
      return 'spinfusor-fire';
    case 'chaingun':
      return 'chaingun-fire';
    case 'mortar':
      return 'mortar-fire';
    case 'blaster':
      return 'blaster-fire';
    case 'laser-rifle':
      return 'laser-rifle-fire';
    case 'plasma':
      return 'plasma-fire';
    case 'grenade-launcher':
      return 'grenade-launcher-fire';
    case 'elf':
      return 'elf-fire';
    case 'shocklance':
      return 'shocklance-fire';
    case 'missile-launcher':
      return 'missile-launcher-fire';
    case 'frag':
      return 'frag-fire';
    case 'concussion':
      return 'concussion-fire';
    default:
      return null;
  }
}

export function getEffectAudioCueId(effectType?: string | null, weaponId?: string | null): string | null {
  switch ((effectType ?? '').toLowerCase()) {
    case 'hitconfirm':
      return 'hit-confirm';
    case 'blockedshot':
      return 'blocked-shot';
    case 'nearmiss':
      return 'near-miss';
    case 'shieldhit':
      return 'shield-hit';
    case 'vehiclehit':
    case 'deployablehit':
      return 'vehicle-damage';
    case 'explosion':
    case 'shockwave':
      if (weaponId === 'mortar' || weaponId === 'missile-launcher') return 'mortar-impact';
      if (weaponId === 'frag' || weaponId === 'concussion' || weaponId === 'grenade-launcher') return 'grenade-impact';
      if (weaponId === 'spinfusor' || weaponId === 'plasma') return 'spinfusor-impact';
      return 'shield-hit';
    case 'deployablepulse':
      return 'deployable-place';
    case 'vehicleburst':
      return 'vehicle-damage';
    // Phase 179: turret fire (base + deployable, incl. AA) now has an audible flak report.
    case 'turrettracer':
      return 'aa-turret-fire';
    default:
      return null;
  }
}

const _loopNodes = new Map<string, { source: AudioBufferSourceNode | OscillatorNode; gain: GainNode }>();

function stopLoop(id: string): void {
  const entry = _loopNodes.get(id);
  if (!entry) return;
  try { entry.gain.disconnect(); } catch {}
  try { entry.source.disconnect(); } catch {}
  _loopNodes.delete(id);
}

export function startSkiLoop(surfaceType: string, speed: number): void {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  stopLoop('ski');
  const bufferSize = ctx.sampleRate * 0.5;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  if (surfaceType === 'rock') { filter.frequency.value = 240; filter.Q.value = 1.2; }
  else if (surfaceType === 'metal') { filter.frequency.value = 800; filter.Q.value = 1.8; }
  else { filter.frequency.value = 500; filter.Q.value = 1.5; }
  const gain = ctx.createGain();
  gain.gain.value = Math.min(0.5, speed / 80 * 0.5);
  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start();
  _loopNodes.set('ski', { source, gain });
}

export function updateSkiLoop(speed: number): void {
  const entry = _loopNodes.get('ski');
  if (!entry) return;
  entry.gain.gain.value = Math.min(0.5, speed / 80 * 0.5);
}

export function stopSkiLoop(): void {
  const entry = _loopNodes.get('ski');
  if (!entry) return;
  const ctx = getAudioContext();
  if (ctx) {
    entry.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.02);
    setTimeout(() => stopLoop('ski'), 150);
  } else {
    stopLoop('ski');
  }
}

export function startJetLoop(): void {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  stopLoop('jet');
  stopLoop('jet-lfo');
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.value = 180;
  osc.frequency.linearRampToValueAtTime(340, ctx.currentTime + 2);
  const lfoOsc = ctx.createOscillator();
  lfoOsc.frequency.value = 8;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.3;
  lfoOsc.connect(lfoGain);
  const gain = ctx.createGain();
  gain.gain.value = 0.25;
  lfoGain.connect(gain.gain);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start();
  lfoOsc.start();
  _loopNodes.set('jet', { source: osc, gain });
  _loopNodes.set('jet-lfo', { source: lfoOsc, gain: lfoGain });
}

export function stopJetLoop(): void {
  const entry = _loopNodes.get('jet');
  if (!entry) return;
  const ctx = getAudioContext();
  if (ctx) {
    entry.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.04);
    setTimeout(() => { stopLoop('jet'); stopLoop('jet-lfo'); }, 200);
  } else {
    stopLoop('jet');
    stopLoop('jet-lfo');
  }
}

export function startWindLoop(): void {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  stopLoop('wind');
  const bufferSize = ctx.sampleRate * 0.5;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 1800;
  const gain = ctx.createGain();
  gain.gain.value = 0.3;
  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start();
  _loopNodes.set('wind', { source, gain });
}

export function stopWindLoop(fadeTime = 0): void {
  const entry = _loopNodes.get('wind');
  if (!entry) return;
  const ctx = getAudioContext();
  if (ctx && fadeTime > 0) {
    entry.gain.gain.setTargetAtTime(0, ctx.currentTime, fadeTime / 3);
    setTimeout(() => stopLoop('wind'), fadeTime * 1000 + 100);
  } else {
    stopLoop('wind');
  }
}

export function startAmbienceLoop(mapId: string): void {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  stopLoop('ambience');
  if (mapId === 'dangerouscrossing') {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 55;
    const gain = ctx.createGain();
    gain.gain.value = 0.04;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    _loopNodes.set('ambience', { source: osc, gain });
  } else {
    const bufferSize = ctx.sampleRate * 0.5;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const filter = ctx.createBiquadFilter();
    if (mapId === 'katabatic') { filter.type = 'lowpass'; filter.frequency.value = 400; }
    else { filter.type = 'bandpass'; filter.frequency.value = 1400; filter.Q.value = 2; }
    const gain = ctx.createGain();
    gain.gain.value = mapId === 'katabatic' ? 0.08 : 0.06;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start();
    _loopNodes.set('ambience', { source, gain });
  }
}

export function stopAmbienceLoop(): void {
  stopLoop('ambience');
  stopLoop('ambience-bird');
  stopLoop('ambience-thunder');
}

// Phase 137 — map-specific ambience cue. Arctic = low wind howl, desert =
// hot wind + distant thunder, verdant = bird chirps + leaves rustle.
// Idempotent — calling repeatedly with the same mapId returns without
// rebuilding the audio graph. Safe to call from match-start.
let _currentAmbienceMap: string | null = null;
export function playMapAmbience(mapId: string): void {
  if (typeof window === 'undefined') return;
  if (_currentAmbienceMap === mapId) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  stopAmbienceLoop();
  _currentAmbienceMap = mapId;
  try {
    if (mapId === 'katabatic') {
      // Cold wind howl: low-passed noise with subtle gentle LFO.
      const bufferSize = ctx.sampleRate * 2;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 280;
      const gain = ctx.createGain();
      gain.gain.value = 0.07;
      source.connect(filter);
      filter.connect(gain);
      gain.connect(getMasterOutput(ctx));
      source.start();
      _loopNodes.set('ambience', { source, gain });
    } else if (mapId === 'dangerouscrossing') {
      // Hot desert wind + a sparse rolling thunder rumble.
      const bufferSize = ctx.sampleRate * 2;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 700;
      filter.Q.value = 1.4;
      const gain = ctx.createGain();
      gain.gain.value = 0.06;
      source.connect(filter);
      filter.connect(gain);
      gain.connect(getMasterOutput(ctx));
      source.start();
      _loopNodes.set('ambience', { source, gain });
      // Distant thunder — 50Hz sub sine, low gain, very slow LFO swell
      const thunderOsc = ctx.createOscillator();
      thunderOsc.type = 'sine';
      thunderOsc.frequency.value = 48;
      const thunderGain = ctx.createGain();
      thunderGain.gain.value = 0.04;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.07;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.035;
      lfo.connect(lfoGain);
      lfoGain.connect(thunderGain.gain);
      thunderOsc.connect(thunderGain);
      thunderGain.connect(getMasterOutput(ctx));
      thunderOsc.start();
      lfo.start();
      _loopNodes.set('ambience-thunder', { source: thunderOsc, gain: thunderGain });
    } else if (mapId === 'raindance') {
      // Verdant: gentle leaf-rustle (bandpass noise) + sparse bird-chirp triangle pulses via LFO.
      const bufferSize = ctx.sampleRate * 2;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 1800;
      filter.Q.value = 1.8;
      const gain = ctx.createGain();
      gain.gain.value = 0.05;
      source.connect(filter);
      filter.connect(gain);
      gain.connect(getMasterOutput(ctx));
      source.start();
      _loopNodes.set('ambience', { source, gain });
      // Bird chirp shimmer — high triangle modulated by slow LFO so it pulses.
      const birdOsc = ctx.createOscillator();
      birdOsc.type = 'triangle';
      birdOsc.frequency.value = 2800;
      const birdGain = ctx.createGain();
      birdGain.gain.value = 0.0;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.3;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.012;
      lfo.connect(lfoGain);
      lfoGain.connect(birdGain.gain);
      birdOsc.connect(birdGain);
      birdGain.connect(getMasterOutput(ctx));
      birdOsc.start();
      lfo.start();
      _loopNodes.set('ambience-bird', { source: birdOsc, gain: birdGain });
    }
  } catch { /* ignore */ }
}

export function stopMapAmbience(): void {
  _currentAmbienceMap = null;
  stopAmbienceLoop();
}
